<template>
  <div>
    <!-- Información General -->
    <b-card
      v-if="productData && !loading"
      no-body
    >
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            xl="12"
            class="mb-2"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h3 class="mb-0">
                  <b-badge
                    variant="primary"
                    pill
                    class="badge-lg"
                  >
                    #{{ productData.code }} - {{ productData.color }}
                  </b-badge>
                </h3>
                <b-badge
                  :variant="productData.is_active === 'Y' ? 'light-success' : 'light-danger'"
                  class="mt-1"
                >
                  {{ productData.is_active === 'Y' ? 'Activo' : 'Inactivo' }}
                </b-badge>
              </div>
              <div>
                <b-button
                  v-b-tooltip.hover
                  variant="flat-dark"
                  title="Editar producto"
                  @click="showEditSidebar = true"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Editar</span>
                </b-button>
                <b-button
                  v-b-tooltip.hover
                  variant="flat-danger"
                  title="Eliminar producto"
                  @click="deleteProduct"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Eliminar</span>
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <!-- Información General -->
          <b-col
            md="6"
            sm="12"
          >
            <h4 class="mb-2 mt-1">
              <feather-icon
                icon="PackageIcon"
                size="20"
                class="mr-1"
              />
              Información General
            </h4>
            <b-list-group striped>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Código:</strong>
                </div>
                <span>{{ productData.code }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="HashIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>SKU:</strong>
                </div>
                <span>{{ productData.sku || 'No definido' }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="FileTextIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Descripción:</strong>
                </div>
                <span>{{ productData.description }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="DollarSignIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Precio Base:</strong>
                </div>
                <span>{{ productData.base_price ? `Q${parseFloat(productData.base_price).toFixed(2)}` : 'No definido' }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="LayersIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Variantes:</strong>
                </div>
                <b-badge variant="warning">
                  {{ productData.product_variants_count || 0 }} variantes
                </b-badge>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Fecha de creación:</strong>
                </div>
                <span>{{ productData.created_at }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="ClockIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Última actualización:</strong>
                </div>
                <span>{{ productData.updated_at }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>

          <!-- Información de Propiedades -->
          <b-col
            md="6"
            sm="12"
          >
            <h4 class="mb-2 mt-1">
              <feather-icon
                icon="GridIcon"
                size="20"
                class="mr-1"
              />
              Propiedades
            </h4>
            <b-list-group striped>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="DropletIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Color:</strong>
                </div>
                <div class="d-flex align-items-center">
                  <div
                    v-if="productData.color_data && productData.color_data.hex_code"
                    class="color-preview mr-2"
                    :style="{ backgroundColor: productData.color_data.hex_code }"
                  />
                  <span>{{ productData.color }}</span>
                </div>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="FolderIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Categoría:</strong>
                </div>
                <b-badge variant="secondary">
                  {{ productData.category }}
                </b-badge>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="MaximizeIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Ancho:</strong>
                </div>
                <span>{{ productData.width || 'No definido' }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <div>
                  <feather-icon
                    icon="TypeIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Nombre Web:</strong>
                </div>
                <span>{{ productData.web_name || 'No definido' }}</span>
              </b-list-group-item>
              <b-list-group-item
                v-if="productData.web_description"
                class="d-flex justify-content-between align-items-start"
              >
                <div>
                  <feather-icon
                    icon="FileTextIcon"
                    size="16"
                    class="mr-1"
                  />
                  <strong>Descripción Web:</strong>
                </div>
                <span class="text-right">{{ productData.web_description }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <h4 class="mb-2 mt-2">
              <feather-icon
                icon="ListIcon"
                size="20"
                class="mr-1"
              />
              Puntos Clave
            </h4>
            <b-list-group striped>
              <b-list-group-item
                v-if="productData.bullet_points"
                class="d-flex justify-content-between align-items-start"
              >
                <div class="text-left">
                  <b-badge
                    v-for="(point, index) in productData.bullet_points.split('\n').filter(p => p.trim())"
                    :key="index"
                    variant="light-primary"
                    class="mr-1"
                  >
                    {{ point.trim() }}
                  </b-badge>
                </div>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Imágenes del Producto -->
    <b-card
      v-if="productData && !loading"
      no-body
    >
      <b-card-body>
        <b-row>
          <b-col cols="12">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                <feather-icon
                  icon="ImageIcon"
                  size="20"
                  class="mr-1"
                />
                Imágenes del Producto
              </h4>
              <b-button
                v-b-tooltip.hover
                variant="flat-dark"
                title="Agregar imagen"
                @click="showImageUpload = true"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-50"
                />
                <span>Agregar</span>
              </b-button>
            </div>

            <div
              v-if="productData && productData.images && productData.images.length > 0"
              class="product-images-container"
            >
              <div
                v-for="image in productData.images"
                :key="image.id"
                class="product-image-item"
              >
                <div class="image-wrapper">
                  <img
                    :src="getImageUrl(image.image_path)"
                    :alt="image.alt_text || 'Imagen del producto'"
                    class="product-image"
                    @error="handleImageError"
                  >
                  <div class="image-overlay">
                    <b-button
                      v-b-tooltip.hover
                      size="sm"
                      variant="info"
                      title="Ver imagen completa"
                      @click="showImageModal(image)"
                    >
                      <feather-icon
                        icon="MaximizeIcon"
                        size="14"
                      />
                    </b-button>
                    <b-button
                      v-if="!image.is_default"
                      v-b-tooltip.hover
                      size="sm"
                      variant="primary"
                      title="Marcar como predeterminada"
                      @click="setDefaultImage(image.id)"
                    >
                      <feather-icon
                        icon="StarIcon"
                        size="14"
                      />
                    </b-button>
                    <b-button
                      v-b-tooltip.hover
                      size="sm"
                      variant="danger"
                      title="Eliminar imagen"
                      @click="deleteImage(image.id)"
                    >
                      <feather-icon
                        icon="TrashIcon"
                        size="14"
                      />
                    </b-button>
                  </div>
                  <b-badge
                    v-if="image.is_default"
                    variant="success"
                    class="default-badge"
                  >
                    Predeterminada
                  </b-badge>
                </div>
              </div>
            </div>

            <div
              v-else
              class="text-center py-4"
            >
              <feather-icon
                icon="ImageIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted">
                No hay imágenes para este producto
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Variantes del Producto -->
    <b-card
      v-if="productData && !loading"
      no-body
    >
      <b-card-body>
        <b-row>
          <b-col cols="12">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                <feather-icon
                  icon="LayersIcon"
                  size="20"
                  class="mr-1"
                />
                Variantes del Producto
              </h4>
              <b-button
                v-b-tooltip.hover
                variant="flat-dark"
                title="Agregar variante"
                @click="openVariantSidebar()"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-50"
                />
                <span>Agregar</span>
              </b-button>
            </div>

            <div v-if="productData && productData.product_variants && productData.product_variants.length > 0">
              <div class="mb-1">
                <b-badge :variant="getInventoryVariant(productInventoryTotals.available_boxes)">
                  {{ productInventoryTotals.available_boxes }} disponibles
                </b-badge>
                <b-badge
                  variant="light-secondary"
                  class="ml-50"
                >
                  {{ productInventoryTotals.reserved_boxes }} reservadas
                </b-badge>
                <b-badge
                  variant="light-secondary"
                  class="ml-50"
                >
                  {{ productInventoryTotals.sold_boxes }} vendidas
                </b-badge>
              </div>
              <b-table
                :items="productData.product_variants"
                :fields="variantFields"
                small
                responsive
                striped
              >
                <template #cell(sku)="data">
                  <strong>{{ data.item.sku }}</strong>
                </template>
                <template #cell(presentation)="data">
                  <b-badge variant="info">
                    {{ data.item.presentation }}
                  </b-badge>
                </template>
                <template #cell(size)="data">
                  <b-badge variant="secondary">
                    {{ data.item.size }}
                  </b-badge>
                </template>
                <template #cell(stock)="data">
                  <div>
                    <b-badge :variant="getInventoryVariant((data.item.stock && data.item.stock.available_boxes) || 0)">
                      {{ (data.item.stock && data.item.stock.available_boxes) || 0 }} uds.
                    </b-badge>
                  </div>
                </template>
                <template #cell(is_big_tall)="data">
                  <b-badge
                    v-if="data.item.is_big_tall"
                    variant="warning"
                  >
                    Big & Tall
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >
                    Regular
                  </span>
                </template>
                <template #cell(active)="data">
                  <b-badge
                    :variant="data.item.active === 'Y' ? 'light-success' : 'light-danger'"
                  >
                    {{ data.item.active === 'Y' ? 'Activa' : 'Inactiva' }}
                  </b-badge>
                </template>
                <template #cell(actions)="data">
                  <div class="d-flex gap-1">
                    <b-button
                      v-b-tooltip.hover
                      variant="flat-primary"
                      size="sm"
                      title="Editar variante"
                      @click="editVariant(data.item)"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-b-tooltip.hover
                      variant="flat-danger"
                      size="sm"
                      title="Eliminar variante"
                      @click="deleteVariant(data.item.id)"
                    >
                      <feather-icon
                        icon="TrashIcon"
                        size="16"
                      />
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <div
              v-else
              class="text-center py-4"
            >
              <feather-icon
                icon="LayersIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted">
                No hay variantes para este producto
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Edit Product Sidebar -->
    <products-edit
      :product-edit-sidebar-active.sync="showEditSidebar"
      :product-id="productId"
      @refreshData="loadProduct"
    />

    <!-- Variant Add/Edit Sidebar -->
    <products-variant-form
      :show.sync="showVariantAdd"
      :product-id="productId"
      :variant-data="selectedVariant"
      @refresh-data="loadProduct"
    />

    <!-- Modal para mostrar imagen en grande -->
    <b-modal
      id="modal-image"
      hide-footer
      centered
      size="xl"
      :title="selectedImage ? selectedImage.alt_text : 'Imagen del producto'"
    >
      <div class="text-center">
        <img
          v-if="selectedImage"
          :src="getImageUrl(selectedImage.image_path)"
          :alt="selectedImage.alt_text"
          class="img-fluid"
          style="max-height: 70vh;"
          @error="handleImageError"
        >
      </div>
    </b-modal>

    <!-- Modal para subir imagen -->
    <b-modal
      v-model="showImageUpload"
      title="Agregar Imagen"
      @ok="uploadImage"
      @hidden="resetImageForm"
    >
      <b-form>
        <b-form-group label="Imagen">
          <b-form-file
            v-model="imageFile"
            accept="image/*"
            placeholder="Seleccionar imagen..."
            drop-placeholder="Arrastrar imagen aquí..."
          />
        </b-form-group>
        <b-form-group label="Texto alternativo">
          <b-form-input
            v-model="imageAltText"
            placeholder="Descripción de la imagen"
          />
        </b-form-group>
        <b-form-checkbox v-model="isDefaultImage">
          Marcar como imagen predeterminada
        </b-form-checkbox>
      </b-form>
    </b-modal>

    <!-- Loading -->
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        Cargando producto...
      </p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="!productData"
      class="text-center py-5"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="48"
        class="text-danger mb-2"
      />
      <p class="text-muted">
        No se pudo cargar el producto
      </p>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import ProductsEdit from './ProductsEdit.vue'
import ProductsVariantForm from './ProductsVariantForm.vue'

export default {
  components: {
    ProductsEdit,
    ProductsVariantForm,
  },
  props: {
    productId: { type: Number, required: true },
  },

  data() {
    return {
      loading: true,
      productData: null,
      showEditSidebar: false,
      showImageUpload: false,
      showVariantAdd: false,
      selectedVariant: null,
      imageFile: null,
      imageAltText: '',
      isDefaultImage: false,
      selectedImage: null,
      variantFields: [
        { key: 'sku', label: 'SKU' },
        { key: 'presentation', label: 'Presentación' },
        { key: 'size', label: 'Talla' },
        { key: 'stock', label: 'Disp.' },
        { key: 'is_big_tall', label: 'Big & Tall' },
        { key: 'active', label: 'Estado' },
        { key: 'actions', label: 'Acciones' },
      ],
    }
  },

  computed: {
    productInventoryTotals() {
      if (!this.productData || !this.productData.product_variants) {
        return { available_boxes: 0, reserved_boxes: 0, sold_boxes: 0 }
      }
      return this.productData.product_variants.reduce((acc, v) => {
        const s = v.stock || { available_boxes: 0, reserved_boxes: 0, sold_boxes: 0 }
        acc.available_boxes += Number(s.available_boxes || 0)
        acc.reserved_boxes += Number(s.reserved_boxes || 0)
        acc.sold_boxes += Number(s.sold_boxes || 0)
        return acc
      }, { available_boxes: 0, reserved_boxes: 0, sold_boxes: 0 })
    },
  },

  created() {
    this.loadProduct()
  },

  methods: {
    async loadProduct() {
      this.loading = true
      this.productData = null

      try {
        const response = await axios.get(`product/${this.productId}`)
        this.productData = response.data.data
      } catch (error) {
        this.showErrors(error)
        this.productData = null
      } finally {
        this.loading = false
      }
    },

    async deleteProduct() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        '¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.',
        {
          title: 'Confirmar eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Sí, eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      )

      if (confirmed) {
        try {
          await axios.delete(`product/${this.productId}`)
          this.$bvToast.toast('Producto eliminado exitosamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true,
          })
          this.$emit('closeTab', this.productId, 'view')
        } catch (error) {
          this.showErrors(error)
        }
      }
    },

    async uploadImage() {
      if (!this.imageFile) {
        this.$bvToast.toast('Debes seleccionar una imagen', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      const formData = new FormData()
      formData.append('image', this.imageFile)
      formData.append('alt_text', this.imageAltText)
      formData.append('is_default', this.isDefaultImage)

      try {
        await axios.post(`product/${this.productId}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        this.$bvToast.toast('Imagen subida exitosamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
        })

        this.showImageUpload = false
        this.loadProduct()
      } catch (error) {
        this.showErrors(error)
      }
    },

    async deleteImage(imageId) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        '¿Estás seguro de que deseas eliminar esta imagen?',
        {
          title: 'Confirmar eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Sí, eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      )

      if (confirmed) {
        try {
          await axios.delete(`product/${this.productId}/images/${imageId}`)
          this.$bvToast.toast('Imagen eliminada exitosamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true,
          })
          this.loadProduct()
        } catch (error) {
          this.showErrors(error)
        }
      }
    },

    async setDefaultImage(imageId) {
      try {
        await axios.put(`product/${this.productId}/images/${imageId}/set-default`)
        this.$bvToast.toast('Imagen marcada como predeterminada', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
        })
        this.loadProduct()
      } catch (error) {
        this.showErrors(error)
      }
    },

    resetImageForm() {
      this.imageFile = null
      this.imageAltText = ''
      this.isDefaultImage = false
    },

    showImageModal(image) {
      this.selectedImage = image
      this.$bvModal.show('modal-image')
    },

    getImageUrl(path) {
      if (!path) return NO_IMAGE_URL
      return buildStorageUrl(path)
    },

    handleImageError(event) {
      const img = event && event.target
      if (img) img.setAttribute('src', NO_IMAGE_URL)
    },

    openVariantSidebar(variant = null) {
      this.selectedVariant = variant
      this.showVariantAdd = true
    },

    editVariant(variant) {
      this.openVariantSidebar({ ...variant })
    },

    getInventoryVariant(value) {
      if (value > 5) return 'light-success'
      if (value > 0) return 'light-warning'
      return 'light-danger'
    },

    async deleteVariant(variantId) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        '¿Estás seguro de que deseas eliminar esta variante? Esta acción no se puede deshacer.',
        {
          title: 'Confirmar eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Sí, eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      )

      if (confirmed) {
        try {
          await axios.delete(`product-variant/${variantId}`)
          this.$bvToast.toast('Variante eliminada exitosamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true,
          })
          this.loadProduct()
        } catch (error) {
          this.showErrors(error)
        }
      }
    },

    showErrors(error) {
      const message = error?.response?.data?.message || error?.message || 'Ha ocurrido un error inesperado'
      this.$bvToast.toast(message, { title: 'Error', variant: 'danger', solid: true })
    },
  },
}
</script>

<style lang="scss" scoped>
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ddd;
}

.product-images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.product-image-item {
  position: relative;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.product-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: 0.5rem;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.default-badge {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
